"use strict";
/**
 * Business day — SPEC §1 (venues run past midnight), §4.1 (denormalised
 * `business_date` on every bill/session/payment), §5.7 (Overall Report defaults
 * to the business day, e.g. 25/7 09:00 -> 26/7 08:00), §6.1 (Opening Time /
 * Closing Time settings).
 *
 * The rule, stated once:
 *
 *   **The business date advances at the venue's Opening Time, in the venue's own
 *   time zone.** Everything from `D 09:00` up to (but not including) `D+1 09:00`
 *   is business date `D`.
 *
 * Consequences:
 *  - A sale at 02:00 belongs to the PREVIOUS calendar date. (§1)
 *  - The closed gap between Closing Time and the next Opening Time (08:00–09:00
 *    for the pilot) still resolves to the business date that just ended, so a
 *    late cash-up or a bill closed after last orders is never lost from reports.
 *  - Business dates partition the timeline exactly: no gaps, no overlaps, so
 *    summing every business day equals summing every transaction.
 *
 * Closing Time defines the *open/trading* window ({@link businessDayOpenHours},
 * {@link isWithinOpenHours}), not the accounting boundary.
 *
 * Time zone is always explicit and IANA-named. The host machine's local time is
 * never consulted — the API container, the staff browser and the .NET agent all
 * compute the same answer.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SECONDS_PER_DAY = exports.DEFAULT_VENUE_TIME_ZONE = void 0;
exports.parseTimeOfDay = parseTimeOfDay;
exports.isTimeOfDay = isTimeOfDay;
exports.isBusinessDate = isBusinessDate;
exports.assertBusinessDate = assertBusinessDate;
exports.addDaysToBusinessDate = addDaysToBusinessDate;
exports.previousBusinessDate = previousBusinessDate;
exports.nextBusinessDate = nextBusinessDate;
exports.zonedPartsOf = zonedPartsOf;
exports.zonedInstant = zonedInstant;
exports.crossesMidnight = crossesMidnight;
exports.resolveBusinessDate = resolveBusinessDate;
exports.currentBusinessDate = currentBusinessDate;
exports.businessDayBounds = businessDayBounds;
exports.businessDaySpanBounds = businessDaySpanBounds;
exports.businessDayOpenHours = businessDayOpenHours;
exports.isWithinOpenHours = isWithinOpenHours;
exports.businessDayLengthSeconds = businessDayLengthSeconds;
/** IANA zone for the pilot venue in Sulaymaniyah (UTC+3, no DST). */
exports.DEFAULT_VENUE_TIME_ZONE = 'Asia/Baghdad';
const SECONDS_PER_DAY = 86_400;
exports.SECONDS_PER_DAY = SECONDS_PER_DAY;
const MS_PER_DAY = 86_400_000;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
/* -------------------------------------------------------------------------- */
/* Parsing & validation                                                       */
/* -------------------------------------------------------------------------- */
/** Seconds since local midnight for an `HH:MM[:SS]` string. */
function parseTimeOfDay(time) {
    if (typeof time !== 'string') {
        throw new TypeError(`Invalid time of day: ${String(time)}`);
    }
    const match = TIME_PATTERN.exec(time.trim());
    if (!match) {
        throw new TypeError(`Invalid time of day "${time}" — expected HH:MM or HH:MM:SS`);
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = match[3] === undefined ? 0 : Number(match[3]);
    if (hours > 23 || minutes > 59 || seconds > 59) {
        throw new RangeError(`Invalid time of day "${time}" — out of range`);
    }
    return hours * 3600 + minutes * 60 + seconds;
}
function isTimeOfDay(value) {
    try {
        parseTimeOfDay(value);
        return true;
    }
    catch {
        return false;
    }
}
/** True for a well-formed, real `YYYY-MM-DD` (rejects `2026-02-30`). */
function isBusinessDate(value) {
    if (typeof value !== 'string' || !DATE_PATTERN.test(value))
        return false;
    const utc = new Date(`${value}T00:00:00.000Z`);
    if (Number.isNaN(utc.getTime()))
        return false;
    return (formatPlainDate({
        year: utc.getUTCFullYear(),
        month: utc.getUTCMonth() + 1,
        day: utc.getUTCDate(),
    }) === value);
}
/** @throws TypeError when `value` is not a valid business date. */
function assertBusinessDate(value) {
    if (!isBusinessDate(value)) {
        throw new TypeError(`Invalid business date "${String(value)}" — expected YYYY-MM-DD`);
    }
}
function toPlainDate(date) {
    const match = DATE_PATTERN.exec(typeof date === 'string' ? date : '');
    if (!match) {
        throw new TypeError(`Invalid business date "${String(date)}" — expected YYYY-MM-DD`);
    }
    const utc = new Date(`${date}T00:00:00.000Z`);
    if (Number.isNaN(utc.getTime())) {
        throw new RangeError(`Invalid business date "${date}"`);
    }
    return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate() };
}
function formatPlainDate(date) {
    const year = String(date.year).padStart(4, '0');
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function addDaysToPlainDate(date, days) {
    const shifted = new Date(utcFromParts(date.year, date.month, date.day, 0, 0, 0) + days * MS_PER_DAY);
    return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
    };
}
/** Calendar arithmetic on a business date. `addDaysToBusinessDate('2026-07-25', -1)`. */
function addDaysToBusinessDate(date, days) {
    if (!Number.isInteger(days))
        throw new TypeError('addDaysToBusinessDate: days must be an integer');
    return formatPlainDate(addDaysToPlainDate(toPlainDate(date), days));
}
function previousBusinessDate(date) {
    return addDaysToBusinessDate(date, -1);
}
function nextBusinessDate(date) {
    return addDaysToBusinessDate(date, 1);
}
/* -------------------------------------------------------------------------- */
/* Time-zone primitives (Intl-based, host-locale independent)                 */
/* -------------------------------------------------------------------------- */
const formatterCache = new Map();
function formatterFor(timeZone) {
    const cached = formatterCache.get(timeZone);
    if (cached)
        return cached;
    let formatter;
    try {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    }
    catch {
        throw new RangeError(`Unknown IANA time zone "${timeZone}"`);
    }
    formatterCache.set(timeZone, formatter);
    return formatter;
}
/** `Date.UTC`, but correct for years 0–99 (which `Date.UTC` maps into the 1900s). */
function utcFromParts(year, month, day, hour, minute, second) {
    const timestamp = Date.UTC(year, month - 1, day, hour, minute, second, 0);
    if (year >= 0 && year < 100) {
        const corrected = new Date(timestamp);
        corrected.setUTCFullYear(year);
        return corrected.getTime();
    }
    return timestamp;
}
/** Wall-clock fields an observer in `timeZone` reads off the clock at `instant`. */
function zonedPartsOf(instant, timeZone = exports.DEFAULT_VENUE_TIME_ZONE) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new TypeError('zonedPartsOf: expected a valid Date');
    }
    const parts = formatterFor(timeZone).formatToParts(instant);
    const read = (type) => {
        const part = parts.find((candidate) => candidate.type === type);
        if (!part)
            throw new RangeError(`Time zone "${timeZone}" produced no "${type}" part`);
        return Number(part.value);
    };
    const era = parts.find((candidate) => candidate.type === 'era');
    const year = read('year');
    return {
        year: era?.value === 'BC' ? 1 - year : year,
        month: read('month'),
        day: read('day'),
        hour: read('hour'),
        minute: read('minute'),
        second: read('second'),
    };
}
/** Offset in ms such that `wallClockMs = utcMs + offset`. */
function zoneOffsetMs(instant, timeZone) {
    const parts = zonedPartsOf(instant, timeZone);
    const wall = utcFromParts(parts.year, parts.month, parts.day, parts.hour, parts.minute, parts.second);
    // Formatter resolution is whole seconds; compare like for like.
    const truncated = Math.floor(instant.getTime() / 1000) * 1000;
    return wall - truncated;
}
/**
 * Convert a venue-local wall clock reading to the UTC instant it denotes.
 * Two passes settle DST transitions; inside a spring-forward gap (a wall time
 * that never happens) the result lands at the equivalent instant after the
 * shift, which is the conventional behaviour.
 */
function zonedWallTimeToUtc(wall, timeZone) {
    const asIfUtc = utcFromParts(wall.year, wall.month, wall.day, wall.hour, wall.minute, wall.second);
    const firstGuess = asIfUtc - zoneOffsetMs(new Date(asIfUtc), timeZone);
    const refinedOffset = zoneOffsetMs(new Date(firstGuess), timeZone);
    return new Date(asIfUtc - refinedOffset);
}
/** The UTC instant at `time` on `date`, read in `timeZone`. */
function zonedInstant(date, time, timeZone = exports.DEFAULT_VENUE_TIME_ZONE) {
    const plain = toPlainDate(date);
    const secondsOfDay = parseTimeOfDay(time);
    return zonedWallTimeToUtc({
        ...plain,
        hour: Math.floor(secondsOfDay / 3600),
        minute: Math.floor((secondsOfDay % 3600) / 60),
        second: secondsOfDay % 60,
    }, timeZone);
}
function resolveConfig(config) {
    const openingSeconds = parseTimeOfDay(config.openingTime);
    const closingSeconds = parseTimeOfDay(config.closingTime);
    return {
        openingSeconds,
        closingSeconds,
        timeZone: config.timeZone ?? exports.DEFAULT_VENUE_TIME_ZONE,
        // Equal opening/closing means a 24h operation: the window runs to the next opening.
        crossesMidnight: closingSeconds <= openingSeconds,
    };
}
/** True when Closing Time lands on the calendar day after Opening Time. */
function crossesMidnight(config) {
    return resolveConfig(config).crossesMidnight;
}
/**
 * Which business date an instant belongs to.
 *
 * ```ts
 * // Venue open 09:00 -> 08:00 next day, Asia/Baghdad
 * resolveBusinessDate(new Date('2026-07-26T02:00:00+03:00'), config); // '2026-07-25'
 * resolveBusinessDate(new Date('2026-07-26T09:00:00+03:00'), config); // '2026-07-26'
 * ```
 *
 * This is the value written to the denormalised `business_date` column at write
 * time (§4.1); reports filter on it, never on `created_at`.
 */
function resolveBusinessDate(instant, config) {
    const { openingSeconds, timeZone } = resolveConfig(config);
    const local = zonedPartsOf(instant, timeZone);
    const secondsOfDay = local.hour * 3600 + local.minute * 60 + local.second;
    const plain = { year: local.year, month: local.month, day: local.day };
    return formatPlainDate(secondsOfDay >= openingSeconds ? plain : addDaysToPlainDate(plain, -1));
}
/** The business date currently in progress. */
function currentBusinessDate(config, now = new Date()) {
    return resolveBusinessDate(now, config);
}
/**
 * Inverse of {@link resolveBusinessDate}: the half-open UTC interval
 * `[start, end)` to put in a `WHERE created_at >= ... AND < ...` clause, or to
 * seed the Overall Report's From/To pickers (§5.7).
 *
 * `end` is the NEXT day's Opening Time — not Closing Time — so the closed gap
 * belongs to exactly one business date and nothing falls between two reports.
 */
function businessDayBounds(businessDate, config) {
    assertBusinessDate(businessDate);
    const { timeZone } = resolveConfig(config);
    return {
        businessDate,
        start: zonedInstant(businessDate, config.openingTime, timeZone),
        end: zonedInstant(nextBusinessDate(businessDate), config.openingTime, timeZone),
    };
}
/** Bounds spanning an inclusive range of business dates, for From/To filters. */
function businessDaySpanBounds(fromBusinessDate, toBusinessDate, config) {
    const from = businessDayBounds(fromBusinessDate, config);
    const to = businessDayBounds(toBusinessDate, config);
    if (to.start.getTime() < from.start.getTime()) {
        throw new RangeError(`businessDaySpanBounds: "${toBusinessDate}" is before "${fromBusinessDate}"`);
    }
    return { from: fromBusinessDate, to: toBusinessDate, start: from.start, end: to.end };
}
/**
 * The advertised trading window — Opening Time to Closing Time. Narrower than
 * {@link businessDayBounds} whenever the venue actually closes; use it for
 * "are we open?" UI, not for reporting.
 */
function businessDayOpenHours(businessDate, config) {
    assertBusinessDate(businessDate);
    const resolved = resolveConfig(config);
    const closingDate = resolved.crossesMidnight ? nextBusinessDate(businessDate) : businessDate;
    return {
        businessDate,
        opensAt: zonedInstant(businessDate, config.openingTime, resolved.timeZone),
        closesAt: zonedInstant(closingDate, config.closingTime, resolved.timeZone),
        crossesMidnight: resolved.crossesMidnight,
    };
}
/** Is the venue open at `instant`? Opening inclusive, closing exclusive. */
function isWithinOpenHours(instant, config) {
    const businessDate = resolveBusinessDate(instant, config);
    const { opensAt, closesAt } = businessDayOpenHours(businessDate, config);
    const time = instant.getTime();
    return time >= opensAt.getTime() && time < closesAt.getTime();
}
/**
 * How long the business day is, in seconds — always `SECONDS_PER_DAY` except
 * across a DST transition, where it is an hour shorter or longer.
 */
function businessDayLengthSeconds(businessDate, config) {
    const { start, end } = businessDayBounds(businessDate, config);
    return Math.round((end.getTime() - start.getTime()) / 1000);
}
//# sourceMappingURL=business-day.js.map